import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, MenuItem, Button, IconButton,
    Chip, Tooltip, CircularProgress, Autocomplete,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";

const COLOR = SECTION_COLORS.rules;

// ─── Serialization ────────────────────────────────────────────────────────────

function dominantKey(items, fallback = "and") {
    const joiners = items.slice(1).map((i) => i.joiner || "and");
    const orCount = joiners.filter((j) => j === "or").length;
    return joiners.length > 0 && orCount > joiners.length / 2 ? "or" : fallback;
}

function serializeItems(items, topKey = "and") {
    const valid = items.filter((i) =>
        i.type === "group" ? (i.conditions || []).some((c) => c.field) : i.field
    );
    if (valid.length === 0) return null;
    const children = valid
        .map((item) => {
            if (item.type === "group") {
                const groupConds = (item.conditions || []).filter((c) => c.field);
                const gType = item.groupType || "or";
                if (gType === "not") {
                    if (groupConds.length === 0) return null;
                    if (groupConds.length === 1) return { not: { field: groupConds[0].field, operator: groupConds[0].operator, value: groupConds[0].value } };
                    return { not: { and: groupConds.map((c) => ({ field: c.field, operator: c.operator, value: c.value })) } };
                }
                if (groupConds.length === 1) return { field: groupConds[0].field, operator: groupConds[0].operator, value: groupConds[0].value };
                const gKey = gType === "and" ? "and" : "or";
                return { [gKey]: groupConds.map((c) => ({ field: c.field, operator: c.operator, value: c.value })) };
            }
            return { field: item.field, operator: item.operator, value: item.value };
        })
        .filter(Boolean);
    if (children.length === 0) return null;
    return { [topKey]: children };
}

export function buildConditionsJson(combinator, rows) {
    if (!rows || rows.length === 0) return null;
    return serializeItems(rows, combinator || "and");
}

function jsonToItems(json, allowGroups = true) {
    if (!json || typeof json !== "object") return [];
    for (const comb of ["and", "or"]) {
        if (comb in json && Array.isArray(json[comb])) {
            return json[comb].map((child, idx) => {
                // NOT group: { not: { field, operator, value } }
                if (allowGroups && "not" in child && !Array.isArray(child.not)) {
                    const notInner = child.not;
                    const conds = "field" in notInner
                        ? [{ id: `nc_${Date.now()}_${idx}`, type: "condition", field: notInner.field || "", operator: notInner.operator || "eq", value: notInner.value ?? "" }]
                        : (notInner.and || notInner.or || []).filter((c) => c && "field" in c).map((c, i) => ({
                            id: `nc_${Date.now()}_${idx}_${i}`, type: "condition",
                            field: c.field || "", operator: c.operator || "eq", value: c.value ?? "",
                            joiner: i > 0 ? "and" : undefined,
                          }));
                    return { id: `g_not_${Date.now()}_${idx}`, type: "group", groupType: "not", joiner: idx > 0 ? comb : undefined, conditions: conds };
                }
                // OR/AND nested group
                const isNested = allowGroups && !("field" in child) && ("and" in child || "or" in child);
                if (isNested) {
                    const innerComb = ["and", "or"].find((k) => k in child) || "or";
                    return {
                        id: `g_${Date.now()}_${idx}`, type: "group", groupType: innerComb,
                        joiner: idx > 0 ? comb : undefined,
                        conditions: (child[innerComb] || []).filter((c) => c && "field" in c).map((c, i) => ({
                            id: `c_${Date.now()}_${idx}_${i}`, type: "condition",
                            field: c.field || "", operator: c.operator || c.op || "eq", value: c.value ?? "",
                            joiner: i > 0 ? innerComb : undefined,
                        })),
                    };
                }
                if ("field" in child) {
                    return { id: `c_${Date.now()}_${idx}`, type: "condition", field: child.field || "", operator: child.operator || child.op || "eq", value: child.value ?? "" };
                }
                return null;
            }).filter(Boolean);
        }
    }
    // Top-level NOT: { not: { field, ... } }
    if ("not" in json && !Array.isArray(json.not)) {
        const notInner = json.not;
        const conds = "field" in notInner
            ? [{ id: `nc_${Date.now()}`, type: "condition", field: notInner.field || "", operator: notInner.operator || "eq", value: notInner.value ?? "" }]
            : [];
        return [{ id: `g_not_${Date.now()}`, type: "group", groupType: "not", conditions: conds }];
    }
    if ("field" in json) {
        return [{ id: `c_${Date.now()}`, type: "condition", field: json.field || "", operator: json.operator || "eq", value: json.value ?? "" }];
    }
    return [];
}

export function parseConditionsJson(conditions) {
    const defaultResult = { combinator: "and", rows: [] };
    if (!conditions || typeof conditions !== "object") return defaultResult;
    const items = jsonToItems(conditions);
    const comb = dominantKey(items);
    return { combinator: comb, rows: items };
}


// ─── ValueEditor ──────────────────────────────────────────────────────────────
function ValueEditor({ field, operator, value, onChange, brandName }) {
    const numericFields = ["customer.lifetime_points","customer.status_points","customer.birthdate_month","customer.birthdate_day","customer.created_at_month","customer.created_at_day","system.today_month","system.today_day","system.customer_created_days","system.customer_last_activity_days"];
    const dateFields = ["customer.last_activity_at","customer.created_at","system.now"];
    const numericPatterns = [/\.metrics\./, /_count/, /_amount/, /_total/, /_score/, /_days/, /_age/, /_points/, /_value/];
    const isStatus = new Set(["status","customer.status"]).has(field);
    const isLoyalty = new Set(["loyalty_status","customer.loyalty_status"]).has(field);
    const isReward = new Set(["rewards","customer.rewards"]).has(field);
    const isGender = new Set(["gender","customer.gender"]).has(field);
    const isEnumField = isStatus || isLoyalty || isReward || isGender;
    const numericOps = new Set(["gt", "gte", "lt", "lte", "between"]);
    const isNumeric = !isEnumField && (
        numericFields.includes(field)
        || numericPatterns.some((p) => p.test(field))
        || (numericOps.has(operator) && !(["customer.birthdate", "customer.last_activity_at", "customer.created_at", "system.now"].includes(field)))
    );
    const isDate = dateFields.includes(field) || field === "customer.birthdate";
    const isDateOnly = field === "customer.birthdate";
    const isArrayOp = ["in","between"].includes(operator);
    const hasDynamicDate = isDate || isDateOnly;
    const isSysObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v) && "$system" in v;
    const initArr = Array.isArray(value) ? value : ["", ""];

    // ── ALL hooks unconditionally at top (Rules of Hooks) ───────────────────
    const [rewardOpts, setRewardOpts] = useState([]);
    const [tierOpts, setTierOpts] = useState([]);
    const [useDynamic, setUseDynamic] = useState(() => hasDynamicDate && !isArrayOp && isSysObj(value));
    const [dynPair, setDynPair] = useState(() => hasDynamicDate && isArrayOp && (isSysObj(initArr[0]) || isSysObj(initArr[1])));

    const sx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const SYS_PRESETS = [
        { k: "today", l: "Aujourd'hui (today)" },
        { k: "now",   l: "Maintenant (now)" },
    ];
    const makeSysObj = (base = "today", add = 0, fmt) => { const o = { $system: base }; if (add !== 0) o.add_days = add; if (fmt) o.format = fmt; return o; };


    useEffect(() => {
        if (!isReward || !brandName) return;
        loyaltyFetch(`${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brandName)}`, { headers: { "X-Brand": brandName } })
            .then((r) => r.ok ? r.json() : []).then((d) => setRewardOpts(Array.isArray(d) ? d : [])).catch(() => {});
    }, [isReward, brandName]);

    useEffect(() => {
        if (!isLoyalty || !brandName) return;
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers?brand=${encodeURIComponent(brandName)}&active=true`, { headers: { "X-Brand": brandName } })
            .then((r) => r.ok ? r.json() : []).then((d) => setTierOpts(Array.isArray(d) ? d : [])).catch(() => {});
    }, [isLoyalty, brandName]);

    if (operator === "exists") return <TextField size="small" fullWidth select label="Existe ?" value={value === true || value === "true" ? "true" : "false"} onChange={(e) => onChange(e.target.value === "true")} sx={sx}><MenuItem value="true">Oui</MenuItem><MenuItem value="false">Non</MenuItem></TextField>;
    if (isGender) return <TextField size="small" fullWidth select label="Genre" value={value ?? ""} onChange={(e) => onChange(e.target.value)} sx={sx}>{["M","F","OTHER","UNKNOWN"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>;
    if (isStatus) return <TextField size="small" fullWidth select label="Statut" value={value ?? ""} onChange={(e) => onChange(e.target.value)} sx={sx}><MenuItem value="ACTIVE">Active</MenuItem><MenuItem value="INACTIVE">Inactive</MenuItem></TextField>;
    if (isLoyalty) {
        const multi = operator === "in";
        const rawSel = multi
            ? (Array.isArray(value) ? value.map(String) : [value].filter(Boolean).map(String))
            : (value != null && value !== "" ? String(value) : "");
        const byKey = new Map(tierOpts.map((t) => [String(t.key), t.name || String(t.key)]));
        return (
            <TextField size="small" fullWidth select label="Statut fidélité"
                value={rawSel}
                onChange={(e) => { const v = e.target.value; onChange(multi ? (Array.isArray(v) ? v : [v].filter(Boolean)) : v); }}
                sx={sx}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                    multiple: multi,
                    displayEmpty: true,
                    renderValue: (s) => multi
                        ? (Array.isArray(s) && s.length ? s.map((k) => byKey.get(String(k)) || k).join(", ") : <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                        : (byKey.get(String(s)) || s || <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                }}>
                {tierOpts.length === 0 && <MenuItem disabled value=""><em>Chargement…</em></MenuItem>}
                {tierOpts.map((t) => <MenuItem key={t.id || t.key} value={String(t.key)}>{t.name || t.key}</MenuItem>)}
            </TextField>
        );
    }
    if (isReward) {
        const multi = operator === "in";
        const rawSel = multi
            ? (Array.isArray(value) ? value.map(String) : [value].filter(Boolean).map(String))
            : (value != null && value !== "" ? String(value) : "");
        const byId = new Map(rewardOpts.map((r) => [String(r.id), r.name || String(r.id)]));
        return (
            <TextField size="small" fullWidth select label="Récompense"
                value={rawSel}
                onChange={(e) => { const v = e.target.value; onChange(multi ? (Array.isArray(v) ? v : [v].filter(Boolean)) : v); }}
                sx={sx}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                    multiple: multi,
                    displayEmpty: true,
                    renderValue: (s) => multi
                        ? (Array.isArray(s) && s.length ? s.map((id) => byId.get(String(id)) || id).join(", ") : <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                        : (byId.get(String(s)) || s || <em style={{color:"#94a3b8"}}>Sélectionner…</em>)
                }}>
                {rewardOpts.length === 0 && <MenuItem disabled value=""><em>Chargement…</em></MenuItem>}
                {rewardOpts.map((r) => <MenuItem key={r.id} value={String(r.id)}>{r.name || r.id}</MenuItem>)}
            </TextField>
        );
    }

    if (hasDynamicDate && isArrayOp) {
        const arrVal = Array.isArray(value) ? value : ["", ""];
        const lo = arrVal[0] ?? ""; const hi = arrVal[1] ?? "";
        const loIsSys = isSysObj(lo); const hiIsSys = isSysObj(hi);
        const updateLo = (v) => onChange([v, hi]);
        const updateHi = (v) => onChange([lo, v]);
        const sysInput = (val, onChg) => (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ p: 1, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
                <TextField select size="small" value={isSysObj(val) ? val.$system : "today"} onChange={(e) => onChg(makeSysObj(e.target.value, isSysObj(val) ? (val.add_days || 0) : 0, isDateOnly ? "mmdd" : (isSysObj(val) ? val.format : undefined)))} sx={{ ...sx, minWidth: 160 }}>
                    {SYS_PRESETS.map((p) => <MenuItem key={p.k} value={p.k}>{p.l}</MenuItem>)}
                </TextField>
                <Typography fontSize={13} color="text.secondary" fontWeight={700}>+</Typography>
                <TextField size="small" type="number" value={isSysObj(val) ? (val.add_days || 0) : 0} onChange={(e) => onChg(makeSysObj(isSysObj(val) ? val.$system : "today", Number(e.target.value), isDateOnly ? "mmdd" : (isSysObj(val) ? val.format : undefined)))} sx={{ ...sx, width: 80 }} />
                <Typography fontSize={12} color="text.secondary" fontWeight={500}>jours</Typography>
                {!isDateOnly && <TextField size="small" label="Format (optionnel)" placeholder="mmdd" value={isSysObj(val) ? (val.format || "") : ""} onChange={(e) => onChg(makeSysObj(isSysObj(val) ? val.$system : "today", isSysObj(val) ? (val.add_days || 0) : 0, e.target.value || undefined))} sx={{ ...sx, width: 120, ml: 1 }} />}
            </Box>
        );
        return (
            <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize={11} color="text.secondary">Mode valeur :</Typography>
                    {[{k:false,l:"Fixe"},{k:true,l:"🔁 Dynamique"}].map(({k,l}) => (
                        <Box key={String(k)} onClick={() => { setDynPair(k); onChange(k ? [makeSysObj("today",0,"mmdd"), makeSysObj("today",7,"mmdd")] : ["",""]); }}
                            sx={{ px:1.5, py:0.3, borderRadius:99, cursor:"pointer", fontSize:11, fontWeight:700, bgcolor: dynPair===k ? COLOR.main : "#f1f5f9", color: dynPair===k ? "#fff" : "#64748b", transition:"all .15s" }}>{l}</Box>
                    ))}
                </Box>
                {dynPair ? (
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Typography fontSize={10} color="text.secondary" fontWeight={600}>Borne basse</Typography>
                        {sysInput(lo, updateLo)}
                        <Typography fontSize={10} color="text.secondary" fontWeight={600}>Borne haute</Typography>
                        {sysInput(hi, updateHi)}
                    </Box>
                ) : operator === "between" ? (
                    <Box display="flex" gap={1}>
                        <TextField size="small" fullWidth label={isDateOnly ? "De (MM-JJ)" : "De"} type={isDateOnly ? "text" : "datetime-local"} InputLabelProps={!isDateOnly ? { shrink: true } : undefined} placeholder={isDateOnly ? "01-01" : ""} value={Array.isArray(value) ? value[0] || "" : ""} onChange={(e) => onChange([e.target.value, Array.isArray(value) ? value[1] || "" : ""])} sx={sx} />
                        <TextField size="small" fullWidth label={isDateOnly ? "À (MM-JJ)" : "À"} type={isDateOnly ? "text" : "datetime-local"} InputLabelProps={!isDateOnly ? { shrink: true } : undefined} placeholder={isDateOnly ? "12-31" : ""} value={Array.isArray(value) ? value[1] || "" : ""} onChange={(e) => onChange([Array.isArray(value) ? value[0] || "" : "", e.target.value])} sx={sx} />
                    </Box>
                ) : (
                    <TextField size="small" fullWidth label="Valeurs (ex: 01-01, 01-31)" value={Array.isArray(value) && !loIsSys && !hiIsSys ? value.join(", ") : ""} onChange={(e) => { const p = e.target.value.split(",").map((s) => s.trim()); onChange(p); }} helperText="Format MM-JJ pour les dates" sx={sx} />
                )}
            </Box>
        );
    }

    if (isArrayOp) {
        if (operator === "between") {
            const arr = Array.isArray(value) ? value : ["", ""];
            return (
                <Box display="flex" gap={1}>
                    <TextField size="small" fullWidth type={isNumeric ? "number" : "text"} label="De" value={arr[0] ?? ""} onChange={(e) => { const v = e.target.value; onChange([isNumeric && v !== "" ? Number(v) : v, arr[1]]); }} sx={sx} />
                    <TextField size="small" fullWidth type={isNumeric ? "number" : "text"} label="À" value={arr[1] ?? ""} onChange={(e) => { const v = e.target.value; onChange([arr[0], isNumeric && v !== "" ? Number(v) : v]); }} sx={sx} />
                </Box>
            );
        }
        const disp = Array.isArray(value) ? value.join(", ") : (value ?? "");
        return <TextField size="small" fullWidth label="Valeurs (séparées par virgule)" value={disp} onChange={(e) => { const p = e.target.value.split(",").map((s) => { const t = s.trim(); const n = Number(t); return t !== "" && !isNaN(n) ? n : t; }); onChange(p); }} helperText="Ex: A, B" sx={sx} />;
    }
    if (isNumeric) return <TextField size="small" fullWidth type="number" label="Valeur" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} sx={sx} helperText={numericOps.has(operator) && !numericFields.includes(field) ? `« ${field} » traité comme numérique` : undefined} />;
    if (hasDynamicDate) {
        // Single date field with optional dynamic mode
        const sysVal = isSysObj(value) ? value : { $system: isDateOnly ? "today" : "now", add_days: 0 };
        return (
            <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontSize={11} color="text.secondary">Mode valeur :</Typography>
                    {[{k:false,l:"Fixe"},{k:true,l:"🔁 Dynamique"}].map(({k,l}) => (
                        <Box key={String(k)} onClick={() => { setUseDynamic(k); onChange(k ? makeSysObj(isDateOnly ? "today" : "now") : ""); }}
                            sx={{ px:1.5, py:0.3, borderRadius:99, cursor:"pointer", fontSize:11, fontWeight:700, bgcolor: useDynamic===k ? COLOR.main : "#f1f5f9", color: useDynamic===k ? "#fff" : "#64748b", transition:"all .15s" }}>{l}</Box>
                    ))}
                </Box>
                {useDynamic ? (
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ p: 1, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }}>
                        <TextField select size="small" value={sysVal.$system || (isDateOnly ? "today" : "now")} onChange={(e) => onChange(makeSysObj(e.target.value, sysVal.add_days || 0, isDateOnly ? "mmdd" : sysVal.format))} sx={{ ...sx, minWidth: 160 }}>
                            {SYS_PRESETS.map((p) => <MenuItem key={p.k} value={p.k}>{p.l}</MenuItem>)}
                        </TextField>
                        <Typography fontSize={13} color="text.secondary" fontWeight={700}>+</Typography>
                        <TextField size="small" type="number" value={sysVal.add_days || 0} onChange={(e) => onChange(makeSysObj(sysVal.$system, Number(e.target.value), isDateOnly ? "mmdd" : sysVal.format))} sx={{ ...sx, width: 80 }} />
                        <Typography fontSize={12} color="text.secondary" fontWeight={500}>jours</Typography>
                        {!isDateOnly && <TextField size="small" label="Format (optionnel)" placeholder="mmdd" value={sysVal.format || ""} onChange={(e) => onChange(makeSysObj(sysVal.$system, sysVal.add_days || 0, e.target.value || undefined))} sx={{ ...sx, width: 120, ml: 1 }} />}
                    </Box>
                ) : (
                    <TextField size="small" fullWidth label={isDateOnly ? "Date" : "Date / Heure"} type={isDateOnly ? "date" : "datetime-local"} InputLabelProps={{ shrink: true }} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} sx={sx} />
                )}
            </Box>
        );
    }
    return <TextField size="small" fullWidth label="Valeur" value={typeof value === "string" ? value : JSON.stringify(value ?? "")} onChange={(e) => onChange(e.target.value)} sx={sx} />;
}

// ─── ConditionRow ─────────────────────────────────────────────────────────────
const NS_COLORS = { payload: "#15803d", customer: "#0369a1", system: "#7c3aed" };
const OP_LABELS = { eq:"= (égal)",neq:"≠ (différent)",gt:"> (supérieur)",gte:"≥ (sup. ou égal)",lt:"< (inférieur)",lte:"≤ (inf. ou égal)",between:"Entre [lo, hi]",in:"Dans la liste",contains:"Contient",exists:"Existe" };

function ConditionRow({ item, onUpdate, onRemove, fieldOptions, operatorOptions, loadingFields, allowCreate, brandName }) {
    const ns = item.field ? item.field.split(".")[0] : null;
    const nsColor = NS_COLORS[ns] || "#e2e8f0";
    const sx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    return (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff", border: "1px solid #e2e8f0", borderLeft: `3px solid ${nsColor}` }}>
            <Box display="flex" gap={1} alignItems="flex-start">
                <Box flex={1} display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Box flex={2} minWidth={180}>
                            <Autocomplete freeSolo={allowCreate} forcePopupIcon size="small" options={fieldOptions}
                                groupBy={(o) => o.startsWith("customer.") ? "Profil client" : o.startsWith("payload.") ? "Payload" : o.startsWith("system.") ? "Système" : "Autres"}
                                getOptionLabel={(o) => !o ? "" : o.includes(".") ? o.substring(o.indexOf(".") + 1) : o}
                                isOptionEqualToValue={(o, v) => o === v}
                                value={item.field || null}
                                loading={loadingFields}
                                onChange={(_, v) => onUpdate(item.id, { field: v || "" })}
                                onInputChange={(_, v, r) => { if (r === "input") onUpdate(item.id, { field: v }); }}
                                renderInput={(p) => (
                                    <TextField {...p} label="Champ" size="small" sx={sx} placeholder="payload.amount"
                                        InputProps={{ ...p.InputProps, endAdornment: <>{loadingFields && <CircularProgress size={14} />}{p.InputProps.endAdornment}</> }} />
                                )}
                                renderOption={(p, o) => {
                                    const k = o.split(".")[0];
                                    return <li {...p} key={o}><Typography fontSize={13} fontWeight={600} color={NS_COLORS[k] || "#334155"} py={0.25}>{o.includes(".") ? o.substring(o.indexOf(".") + 1) : o}</Typography></li>;
                                }}
                            />
                        </Box>
                        <Box flex={1} minWidth={130}>
                            <TextField select size="small" fullWidth label="Opérateur" value={item.operator || "eq"} onChange={(e) => onUpdate(item.id, { operator: e.target.value })} sx={sx}>
                                {operatorOptions.map((op) => <MenuItem key={op} value={op}><Typography fontSize={12}>{OP_LABELS[op] || op}</Typography></MenuItem>)}
                            </TextField>
                        </Box>
                    </Box>
                    {item.field && <ValueEditor field={item.field} operator={item.operator || "eq"} value={item.value} brandName={brandName} onChange={(v) => onUpdate(item.id, { value: v })} />}
                    {ns && <Typography fontSize={10} color={nsColor} fontWeight={600} px={0.5}>{ns === "payload" ? "📦 Payload" : ns === "customer" ? "👤 Profil client" : "⚙️ Système"}</Typography>}
                </Box>
                <IconButton size="small" onClick={() => onRemove(item.id)} sx={{ color: "#dc2626", mt: 0.5 }}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
        </Box>
    );
}

// ─── GroupBox ─────────────────────────────────────────────────────────────────
function GroupBox({ item, onUpdateGroup, onRemoveGroup, fieldOptions, operatorOptions, loadingFields, allowCreate, brandName }) {
    const conds = item.conditions || [];

    const addCond = () => onUpdateGroup(item.id, {
        conditions: [...conds, { id: Date.now(), type: "condition", field: "", operator: "eq", value: "", joiner: "and" }],
    });
    const removeCond = (id) => onUpdateGroup(item.id, { conditions: conds.filter((c) => c.id !== id) });
    const updateCond = (id, patch) => onUpdateGroup(item.id, { conditions: conds.map((c) => c.id === id ? { ...c, ...patch } : c) });
    const updateJoiner = (id, joiner) => onUpdateGroup(item.id, { conditions: conds.map((c) => c.id === id ? { ...c, joiner } : c) });

    const shared = { fieldOptions, operatorOptions, loadingFields, allowCreate, brandName };

    const groupType = item.groupType || "or";
    const setGroupType = (gt) => onUpdateGroup(item.id, { groupType: gt });
    const GT_OPTIONS = [
        { k: "or",  l: "OU",  tip: "Au moins une condition vraie",  bg: "#f59e0b" },
        { k: "and", l: "ET",  tip: "Toutes les conditions vraies",  bg: COLOR.main },
        { k: "not", l: "NON", tip: "Aucune / négation de la condition", bg: "#dc2626" },
    ];
    const gtMeta = GT_OPTIONS.find((o) => o.k === groupType) || GT_OPTIONS[0];

    return (
        <Box sx={{ borderRadius: 2, border: `1.5px dashed ${gtMeta.bg}60`, bgcolor: "#f8fafc", p: 1.5 }}>
            {/* Group header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center" gap={0.75}>
                    <FolderOpenIcon sx={{ fontSize: 15, color: gtMeta.bg }} />
                    <Box sx={{ fontFamily: "monospace", bgcolor: `${gtMeta.bg}20`, px: 0.75, py: 0.2, borderRadius: 1, fontSize: 13, fontWeight: 800, color: gtMeta.bg }}>( </Box>
                    {/* Type selector */}
                    <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                        {GT_OPTIONS.map(({ k, l, bg }) => (
                            <Tooltip key={k} title={GT_OPTIONS.find((o) => o.k === k)?.tip || ""}>
                                <Box onClick={() => setGroupType(k)} sx={{
                                    px: 1.5, py: 0.3, cursor: "pointer", fontSize: 10, fontWeight: 800,
                                    bgcolor: groupType === k ? bg : "#fff", color: groupType === k ? "#fff" : "#94a3b8",
                                    transition: "all .15s", userSelect: "none",
                                    "&:hover": { bgcolor: groupType === k ? bg : "#f8fafc", color: groupType === k ? "#fff" : "#475569" },
                                }}>{l}</Box>
                            </Tooltip>
                        ))}
                    </Box>
                    {groupType === "not" && <Typography fontSize={10} color="#dc2626" fontWeight={600}>⚠ Max 1 condition</Typography>}
                </Box>
                <Box display="flex" gap={0.5} alignItems="center">
                    {groupType !== "not" && <Button size="small" onClick={addCond}
                        sx={{ fontSize: 10, textTransform: "none", color: COLOR.main, minWidth: 0, px: 1 }}>+ Condition</Button>}
                    {groupType === "not" && conds.length === 0 && <Button size="small" onClick={addCond}
                        sx={{ fontSize: 10, textTransform: "none", color: "#dc2626", minWidth: 0, px: 1 }}>+ Condition</Button>}
                    <IconButton size="small" onClick={() => onRemoveGroup(item.id)} sx={{ color: "#dc2626" }}>
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </Box>
            </Box>

            {conds.length === 0 && (
                <Typography fontSize={11} color="text.secondary" textAlign="center" py={1}>
                    Groupe vide — ajoutez des conditions ci-dessus.
                </Typography>
            )}

            {conds.map((cond, idx) => (
                <React.Fragment key={cond.id}>
                    {idx > 0 && (
                        <Box display="flex" justifyContent="center" alignItems="center" my={0.75} gap={0.5}>
                            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
                            <Box sx={{
                                px: 2, py: 0.3, borderRadius: 99, fontSize: 11, fontWeight: 800,
                                bgcolor: groupType === "or" ? "#f59e0b" : COLOR.main,
                                color: "#fff", userSelect: "none", letterSpacing: 0.5, opacity: groupType === "not" ? 0.5 : 1
                            }}>
                                {groupType === "or" ? "OU" : groupType === "not" ? "NON" : "ET"}
                            </Box>
                            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
                        </Box>
                    )}
                    <ConditionRow item={cond} onUpdate={updateCond} onRemove={removeCond} {...shared} />
                </React.Fragment>
            ))}

            {/* Group footer */}
            <Box display="flex" justifyContent="flex-end" mt={1}>
                <Box sx={{ fontFamily: "monospace", bgcolor: "#e2e8f0", px: 0.75, py: 0.2, borderRadius: 1, fontSize: 13, fontWeight: 800, color: "#475569" }}> )</Box>
            </Box>
        </Box>
    );
}

// ─── ConditionBuilder ─────────────────────────────────────────────────────────
export default function ConditionBuilder({
    brandName, transactionType, conditionCatalog, uiHints,
    combinator, rows, onChange,
    payloadPaths, hasPayloadSchema,
}) {
    const [fieldOpts, setFieldOpts] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);

    const fieldDatasource = uiHints?.field?.datasource || {
        endpoint: "/admin/rules/ui-options/condition-fields",
        brandVia: "X-Brand",
        valueField: "items",
        dependsOn: { transaction_type: "transaction_type" },
    };
    const allowCreate = uiHints?.field?.allowCreate !== false;
    const operatorOptions = uiHints?.operator?.options || ["eq","neq","gt","gte","lt","lte","between","in","contains","exists"];

    useEffect(() => {
        if (!brandName || !transactionType) { setFieldOpts([]); return; }
        const url = new URL(`${LOYALTY_API_BASE}${fieldDatasource.endpoint}`);
        if (fieldDatasource.dependsOn?.transaction_type) url.searchParams.set("transaction_type", transactionType);
        const headers = fieldDatasource.brandVia ? { [fieldDatasource.brandVia]: brandName } : {};
        setLoadingFields(true);
        loyaltyFetch(url.toString(), { headers })
            .then((r) => r.ok ? r.json() : null)
            .then((res) => { const items = res?.[fieldDatasource.valueField || "items"]; setFieldOpts(Array.isArray(items) ? items : []); })
            .catch(() => setFieldOpts([]))
            .finally(() => setLoadingFields(false));
    }, [brandName, transactionType]);

    const sortedFieldOpts = React.useMemo(() =>
        [...fieldOpts].sort((a, b) => {
            const g = (o) => o.startsWith("customer.") ? 1 : o.startsWith("payload.") ? 2 : o.startsWith("system.") ? 3 : 4;
            return g(a) !== g(b) ? g(a) - g(b) : a.localeCompare(b);
        }), [fieldOpts]);
    // ── top-level global combinator ────────────────────────────────────────────
    const [topCombinator, setTopCombinator] = useState(combinator || "and");
    useEffect(() => { setTopCombinator(combinator || "and"); }, [combinator]);

    const emit = (next, tc = topCombinator) => {
        onChange(tc, next);
    };

    const addCondition = () => emit([...rows, { id: Date.now(), type: "condition", field: "", operator: "eq", value: "" }]);

    const addGroup = () => emit([...rows, {
        id: `g_${Date.now()}`,
        type: "group",
        conditions: [{ id: Date.now(), type: "condition", field: "", operator: "eq", value: "" }],
    }]);

    const removeItem = (id) => emit(rows.filter((r) => r.id !== id));

    const updateItem = (id, patch) => emit(rows.map((r) => r.id === id ? { ...r, ...patch } : r));

    const updateGroup = (groupId, patch) => emit(rows.map((r) => r.id === groupId ? { ...r, ...patch } : r));

    const changeTopCombinator = (tc) => {
        setTopCombinator(tc);
        emit(rows, tc);
    };

    // ── logic summary ──────────────────────────────────────────────────────────
    const hasGroup = rows.some((r) => r.type === "group");

    const shared = { fieldOptions: sortedFieldOpts, operatorOptions, loadingFields, allowCreate, brandName };

    return (
        <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${COLOR.main}30`, bgcolor: "#fafaff" }}>

            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <FilterAltIcon sx={{ fontSize: 18, color: COLOR.main }} />
                    <Typography fontSize={13} fontWeight={700} color="#334155">Conditions de la règle</Typography>
                    {loadingFields && <CircularProgress size={13} sx={{ color: COLOR.main }} />}
                    {hasGroup && (
                        <Chip label="( ) Groupes actifs" size="small"
                            sx={{ fontSize: 10, fontWeight: 700, height: 20, bgcolor: "#f3e8ff", color: "#7c3aed", border: "1px solid #d8b4fe" }} />
                    )}
                </Box>

                {/* Global combinator toggle — applies to ALL top-level items */}
                {rows.length > 1 && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize={11} color="text.secondary" fontWeight={600}>Relier par :</Typography>
                        <Box sx={{ display: "flex", borderRadius: 99, overflow: "hidden", border: `1px solid ${COLOR.main}40`, boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
                            {[{ k: "and", l: "ET (AND)", ac: COLOR.main }, { k: "or", l: "OU (OR)", ac: "#f59e0b" }].map(({ k, l, ac }) => (
                                <Box key={k} onClick={() => changeTopCombinator(k)} sx={{
                                    px: 2, py: 0.5, cursor: "pointer", fontSize: 11, fontWeight: 800,
                                    bgcolor: topCombinator === k ? ac : "#fff",
                                    color: topCombinator === k ? "#fff" : "#94a3b8",
                                    transition: "all .15s", userSelect: "none",
                                    "&:hover": { bgcolor: topCombinator === k ? ac : "#f8fafc", color: topCombinator === k ? "#fff" : "#475569" },
                                }}>{l}</Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Box display="flex" gap={0.75}>
                    <Tooltip title={!transactionType ? "Sélectionnez un type de transaction" : "Ajouter une condition"}>
                        <span>
                            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addCondition}
                                disabled={!transactionType}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 12, color: COLOR.main }}>
                                Condition
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={!transactionType ? "Sélectionnez un type de transaction" : "Ajouter un groupe ( … )"}>
                        <span>
                            <Button size="small" startIcon={<FolderOpenIcon />} onClick={addGroup}
                                disabled={!transactionType}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 12, color: "#7c3aed", border: "1px solid #d8b4fe", bgcolor: "#faf5ff" }}>
                                Groupe
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Empty states */}
            {!transactionType && <Typography fontSize={12} color="text.secondary" textAlign="center" py={1.5}>Sélectionnez un type de transaction pour configurer les conditions.</Typography>}
            {transactionType && rows.length === 0 && <Typography fontSize={12} color="text.secondary" textAlign="center" py={1.5}>Aucune condition — la règle s'appliquera à toutes les transactions.</Typography>}

            {/* Items — top-level combinator shown as fixed badge between items */}
            {rows.map((item, idx) => (
                <React.Fragment key={item.id}>
                    {idx > 0 && (
                        <Box display="flex" justifyContent="center" alignItems="center" my={0.75} gap={0.5}>
                            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
                            <Box sx={{
                                px: 2, py: 0.3, borderRadius: 99, fontSize: 11, fontWeight: 800,
                                bgcolor: topCombinator === "or" ? "#f59e0b" : COLOR.main,
                                color: "#fff", userSelect: "none", letterSpacing: 0.5,
                            }}>
                                {topCombinator === "or" ? "OU" : "ET"}
                            </Box>
                            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
                        </Box>
                    )}
                    {item.type === "group"
                        ? <GroupBox item={item} onUpdateGroup={updateGroup} onRemoveGroup={removeItem} {...shared} />
                        : <ConditionRow item={item} onUpdate={updateItem} onRemove={removeItem} {...shared} />
                    }
                </React.Fragment>
            ))}
        </Box>
    );
}
